// src/user/LoginEmail.jsx
import React, { useState } from "react";
import { useForm } from "../hooks/useForm";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { ModalEULA } from "./modalEULA";

// ✅ Toast global (si lo prefieres en login puedes no usarlo; mantengo mensajes inline como el legacy)
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack, TextField, Button, InputAdornment } from "@mui/material";

// Icons
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";

export const LoginMail = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast(); // opcional (lo uso sólo para fallos “duros” si quieres)
  const { formulario, cambiado } = useForm();
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const [logeado, setLogeado] = useState("no_logeado"); // "no_logeado" | "logeado" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // EULA
  const [usuarioPendienteEULA, setUsuarioPendienteEULA] = useState(null);

  // =========================
  // Login
  // =========================
  const logUser = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMsg("");
    setLogeado("no_logeado");

    // Normaliza email en el submit
    const payload = {
      email: (formulario.email || "").trim().toLowerCase(),
      password: formulario.password || "",
    };

    try {
      const request = await fetch(`${Global.url}usuarios/login-email`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });

      const data = await request.json().catch(() => ({}));

      if (request.ok) {
        // Si falta EULA, guardamos token provisional
        if (!data.user?.eula_aceptado) {
          localStorage.setItem("token", data.token);
          setUsuarioPendienteEULA(data.user);
          return;
        }

        // Login OK
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setLogeado("logeado");

        setTimeout(() => {
          setAuth(data.user);
          navigate("/app/inicio", { replace: true });
        }, 600);
      } else {
        setLogeado("error");
        setErrorMsg(data?.message || "Error en la identificación");
      }
    } catch (error) {
      console.error("Error al iniciar sesión (Email):", error);
      setLogeado("error");
      setErrorMsg("Error de conexión con el servidor");
      toast?.error?.("Error de conexión con el servidor."); // opcional
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // EULA
  // =========================
  const aceptarEULA = async () => {
    try {
      const request = await fetch(`${Global.url}usuarios/eula`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!request.ok) throw new Error("Error al aceptar el EULA");

      const updatedUser = { ...usuarioPendienteEULA, eula_aceptado: true };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setAuth(updatedUser);
      navigate("/app/inicio", { replace: true });
    } catch (error) {
      console.error("Error al aceptar EULA:", error);
      localStorage.clear();
      setUsuarioPendienteEULA(null);
      setLogeado("error");
      setErrorMsg("No se pudo registrar la aceptación del EULA");
    }
  };

  const rechazarEULA = () => {
    localStorage.clear();
    setUsuarioPendienteEULA(null);
    setLogeado("no_logeado");
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        {/* Cabecera */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Iniciar sesión
          </Typography>

          {logeado === "logeado" && (
            <Typography sx={{ mt: 0.5, fontWeight: 600, color: "success.main" }}>
              Usuario identificado correctamente
            </Typography>
          )}

          {logeado === "error" && (
            <Typography sx={{ mt: 0.5, fontWeight: 600 }} color="error">
              {errorMsg || "Error en la identificación"}
            </Typography>
          )}
        </Box>

        {/* ✅ Form single-column semántico (tema) */}
        <Box className="contentContainer contentContainer--formSingle">
          <Paper sx={{ p: 2 }}>
            <Box component="form" onSubmit={logUser}>
              <Stack spacing={2}>
                {/* Email */}
                <TextField
                  name="email"
                  type="email"
                  label="Email"
                  placeholder="usuario@dominio.com"
                  onChange={cambiado}
                  autoComplete="username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password */}
                <TextField
                  name="password"
                  type="password"
                  label="Contraseña"
                  placeholder="Contraseña"
                  onChange={cambiado}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Acciones */}
                <Stack spacing={1} sx={{ pt: 0.5 }}>
                  <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting} sx={{ height: 40 }}>
                    {isSubmitting ? "Entrando..." : "Identifícate"}
                  </Button>

                  <Typography sx={{ textAlign: "center", fontSize: 14 }}>
                    <Link to="/olvide-password">¿Has olvidado tu contraseña?</Link>
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>

      {/* EULA */}
      {usuarioPendienteEULA && <ModalEULA onAccept={aceptarEULA} onReject={rechazarEULA} />}
    </Box>
  );
};
